import React from 'react';

const PRIMARY = '#1976d2';
const PRIMARY_DARK = '#1565c0';
const ORANGE = '#ff9800';
const LIGHT_BLUE = '#e3f2fd';
const BORDER = '#e0e0e0';
const GRAY = '#757575';
const DARK = '#333';
const LIGHT_GRAY = '#f5f5f5';

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

// --- SHARED TABLE ---

const Table = ({
  headers,
  children
}: {
  headers: string[];
  children: React.ReactNode;
}) => (
  <table
    style={{
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '12.5px',
      marginBottom: '16px'
    }}>
    <thead>
      <tr>
        {headers.map((h, i) => (
          <th
            key={i}
            style={{
              backgroundColor: PRIMARY_DARK,
              color: 'white',
              padding: '8px 10px',
              textAlign: 'center',
              fontWeight: '600',
              fontSize: '12px',
              border: `1px solid ${PRIMARY_DARK}`
            }}>
            {h}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>{children}</tbody>
  </table>
);

const Tr = ({ children, even }: { children: React.ReactNode; even?: boolean }) => (
  <tr style={{ backgroundColor: even ? LIGHT_GRAY : 'white' }}>{children}</tr>
);

const Td = ({
  children,
  style,
  center
}: {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  center?: boolean;
}) => (
  <td
    style={{
      padding: '7px 10px',
      border: `1px solid ${BORDER}`,
      textAlign: center ? 'center' : 'left',
      verticalAlign: 'middle',
      ...style
    }}>
    {children}
  </td>
);

// --- PAGE HEADER (every page) ---

export const PageHeader = ({ data }: { data: any }) => (
  <div style={{ marginBottom: '10px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
      <div>
        <div style={{ color: PRIMARY, fontWeight: '700', fontSize: '13px' }}>
          {data.organization_name}
        </div>
        <div style={{ color: GRAY, fontSize: '11px' }}>Training Completion Report</div>
      </div>
      <div style={{ color: GRAY, fontSize: '11px' }}>
        {formatDate(data.start_date)} - {formatDate(data.end_date)}
      </div>
    </div>
    <div style={{ borderBottom: `2px solid ${PRIMARY}`, marginTop: '6px' }} />
  </div>
);

// --- PAGE FOOTER ---

export const PageFooter = ({ data }: { data: any }) => (
  <div style={{ marginTop: '20px' }}>
    <div style={{ borderTop: `1px solid ${BORDER}` }} />
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: '6px',
        fontSize: '11px',
        color: GRAY
      }}>
      <span>
        Report Date: <strong style={{ color: DARK }}>{formatDate(data.report_date)}</strong>
      </span>
    </div>
  </div>
);

// --- SECTION HEADER: left blue bar style (for text sections: intro, ceremony, remarks, annex) ---

export const TextSectionHeader = ({ title }: { title: string }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      margin: '0 0 14px',
      borderLeft: `4px solid ${PRIMARY}`,
      paddingLeft: '12px'
    }}>
    <h3 style={{ color: PRIMARY, fontWeight: '700', fontSize: '17px', margin: 0 }}>{title}</h3>
  </div>
);

// --- SECTION HEADER: numbered box style (for data sections: schedule, feedback, attendance, stats) ---

export const SectionHeader = ({ title }: { title: string }) => {
  const match = title.match(/^(\d+\.?\d*)\s*(.*)/);
  const number = match ? match[1].replace(/\.$/, '') : '';
  const label = match ? match[2] : title;

  if (!number) {
    return <TextSectionHeader title={title} />;
  }

  const isDecimal = number.includes('.');
  if (isDecimal) {
    // e.g., "2.1" → box + title
    return (
      <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 16px' }}>
        <div
          style={{
            backgroundColor: PRIMARY,
            color: 'white',
            fontWeight: '700',
            fontSize: '14px',
            padding: '8px 12px',
            marginRight: '12px',
            borderRadius: '4px',
            flexShrink: 0
          }}>
          {number}
        </div>
        <h3 style={{ color: PRIMARY, fontWeight: '700', fontSize: '18px', margin: 0 }}>{label}</h3>
      </div>
    );
  }

  // Whole number → box + title
  return (
    <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 16px' }}>
      <div
        style={{
          backgroundColor: PRIMARY,
          color: 'white',
          fontWeight: '700',
          fontSize: '15px',
          minWidth: '42px',
          height: '42px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '12px',
          borderRadius: '4px',
          flexShrink: 0
        }}>
        {number}
      </div>
      <h3 style={{ color: PRIMARY, fontWeight: '700', fontSize: '18px', margin: 0 }}>{label}</h3>
    </div>
  );
};

// --- REPORT SECTION (text content with optional image) ---

export const ReportSection = ({ title, content, image, baseUrl }: any) => {
  if (!content && !image) return null;

  const label = title.replace(/^\d+\.?\s*/, '');

  return (
    <section style={{ marginBottom: '32px' }}>
      <TextSectionHeader title={title} />
      {image && (
        <div style={{ marginBottom: '16px' }}>
          <figure
            style={{
              border: `1px solid ${BORDER}`,
              borderRadius: '4px',
              overflow: 'hidden',
              margin: 0
            }}>
            <img
              src={`${baseUrl}/${image}`}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '400px',
                objectFit: 'contain',
                display: 'block'
              }}
              alt={label}
            />
            <figcaption
              style={{
                textAlign: 'center',
                padding: '8px',
                backgroundColor: LIGHT_GRAY,
                fontSize: '12px',
                fontWeight: '600',
                color: DARK
              }}>
              {label}
            </figcaption>
          </figure>
        </div>
      )}
      <div
        className="rich-content"
        style={{ lineHeight: '1.8', color: DARK, fontSize: '13px' }}
        dangerouslySetInnerHTML={{ __html: content || '' }}
      />
    </section>
  );
};

// --- COVER PAGE ---

export const CoverPage = ({ data }: { data: any }) => {
  const getInitial = (name: string) => (name ? name.charAt(0).toUpperCase() : '?');

  return (
    <div
      style={{
        fontFamily: 'Arial, Helvetica, sans-serif',
        minHeight: '240mm',
        display: 'flex',
        flexDirection: 'column'
      }}>
      {/* Blue banner */}
      <div
        style={{
          backgroundColor: PRIMARY,
          color: 'white',
          padding: '20px 28px',
          marginBottom: '48px',
          borderRadius: '4px',
          justifyItems: 'center'
        }}>
        <div style={{ fontWeight: '700', fontSize: '22px' }}>{data.organization_name}</div>
        <div style={{ fontSize: '13px', opacity: 0.9, marginTop: '2px' }}>
          {data.location || data.center || ''}
        </div>
      </div>

      {/* Main title block */}
      <div style={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <h1
          style={{
            color: PRIMARY,
            fontWeight: '700',
            fontSize: '36px',
            marginBottom: '20px',
            lineHeight: 1.2
          }}>
          Training Completion Report
        </h1>

        <h2
          style={{
            fontWeight: '700',
            fontSize: '20px',
            color: DARK,
            margin: '0 0 4px'
          }}>
          {data.training_name}
        </h2>

        {data.training_code && (
          <div style={{ color: GRAY, fontSize: '14px', marginBottom: '10px' }}>
            ({data.training_code})
          </div>
        )}

        <div
          style={{
            color: ORANGE,
            fontWeight: '700',
            fontSize: '16px',
            marginBottom: '6px'
          }}>
          Batch: {data.batch_name}
        </div>

        Training Date
        <div style={{ color: GRAY, fontSize: '14px', marginBottom: '40px' }}>
          {formatDate(data.start_date)} - {formatDate(data.end_date)}
        </div>

        {/* Training Faculty */}
        {data.faculty?.length > 0 && (
          <div style={{ marginBottom: '48px' }}>
            <h3
              style={{
                color: PRIMARY,
                fontWeight: '600',
                fontSize: '16px',
                marginBottom: '20px'
              }}>
              Course Coordinator
            </h3>
            <div
              style={{
                display: 'flow',
                justifyContent: 'center',
                gap: '24px',
                // flexWrap: 'wrap'
              }}>
              {data.faculty.map((f: any, i: number) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  {/* <div
                    style={{
                      width: '80px',
                      height: '80px',
                      border: `1px solid ${BORDER}`,
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: LIGHT_BLUE,
                      margin: '0 auto 8px',
                      overflow: 'hidden'
                    }}>
                    {f.image_path ? (
                      <img
                        src={f.image_path}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        alt={f.name}
                      />
                    ) : (
                      <span style={{ color: PRIMARY, fontWeight: '700', fontSize: '30px' }}>
                        {getInitial(f.name)}
                      </span>
                    )}
                  </div> */}
                  <div style={{ fontWeight: '600', fontSize: '12.5px', color: DARK }}>{f.name}</div>
                  {/* {f.position && (
                    <div style={{ fontSize: '11px', color: GRAY }}>{f.position}</div>
                  )} */}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Submitted To / By */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div
            style={{
              border: `1px solid ${BORDER}`,
              borderRadius: '4px',
              padding: '16px 28px',
              minWidth: '220px',
              textAlign: 'left',
              justifyItems: 'center'
            }}>
            <div
              style={{
                color: PRIMARY,
                fontWeight: '700',
                fontSize: '13px',
                marginBottom: '4px'
              }}>
              Submitted To
            </div>
            <div
              className="rich-content"
              style={{ color: DARK, fontSize: '13px', marginBottom: '12px' }}
              dangerouslySetInnerHTML={{ __html: data.submitted_to || '' }}
            />
            <div style={{ borderTop: `1px solid ${BORDER}`, margin: '8px 0' }} />
            <div
              style={{
                color: PRIMARY,
                fontWeight: '700',
                fontSize: '13px',
                marginBottom: '4px'
              }}>
              Submitted By
            </div>
            <div
              className="rich-content"
              style={{ color: DARK, fontSize: '13px' }}
              dangerouslySetInnerHTML={{ __html: data.submitted_by || '' }}
            />
            <div style={{ color: PRIMARY, fontStyle: 'italic', fontSize: '12px', marginTop: '4px' }}>
              {formatDate(data.report_date)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- TABLE OF CONTENTS ---

export const TableOfContents = ({ data }: { data: any }) => {

  type TocItem = { label: string; sub?: boolean };
  const items: TocItem[] = [
    { label: '1. Introduction' },
    { label: '2. Training Details' },
    ...(data.trainees?.length > 0 ? [{ label: '2.1 Participants Details', sub: true }] : []),
    { label: '3. Opening Ceremony' },
    ...(data.modules?.length > 0 ? [{ label: '4. Program Schedule' }] : []),
    { label: '5. Feedback Report' },
    { label: '6. Attendance Report' },
    ...(data.trainees?.[0]?.post_evaluation?.length > 0 ? [{ label: '7. Pre/Post Evaluation Report' }] : []),
    { label: '8. Statistics' },
    { label: '9. Closing Ceremony' },
    { label: "10. Coordinators' Remarks" },
    ...(data.additional_information ? [{ label: '11. Additional Information' }] : []),
    { label: 'Annex' }
  ];

  return (
    <div>
      <div
        style={{
          backgroundColor: PRIMARY,
          color: 'white',
          textAlign: 'center',
          padding: '16px',
          fontWeight: '700',
          fontSize: '22px',
          marginBottom: '24px',
          borderRadius: '4px'
        }}>
        Table of Contents
      </div>
      <div>
        {items.map((item, i) => (
          <div
            key={i}
            style={{
              border: `1px solid ${BORDER}`,
              borderRadius: '4px',
              padding: '11px 16px',
              marginBottom: '8px',
              paddingLeft: item.sub ? '32px' : '16px'
            }}>
            <span style={{ fontSize: '13px', color: DARK }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- TRAINEE TABLE ---

export const TraineeTable = ({ trainees }: { trainees: any[] }) => (
  <Table headers={['SN', 'Name', 'Employee ID', 'Mobile', 'Email']}>
    {trainees.map((t, i) => (
      <Tr key={i} even={i % 2 === 1}>
        <Td center style={{ width: '40px' }}>
          {i + 1}
        </Td>
        <Td>
          <strong>{t.full_name}</strong>
        </Td>
        <Td center>{t.employee_id || '-'}</Td>
        <Td>{t.mobile}</Td>
        <Td>{t.email}</Td>
      </Tr>
    ))}
  </Table>
);

// --- PROGRAM SCHEDULE ---

export const ProgramSchedule = ({ modules }: { modules: any[] }) => (
  <div>
    {modules.map((mod, mIdx) => (
      <div key={mIdx} style={{ marginBottom: '28px' }}>
        {/* Module header */}
        <div
          style={{
            backgroundColor: LIGHT_BLUE,
            borderLeft: `4px solid ${PRIMARY}`,
            padding: '10px 16px',
            marginBottom: '10px',
            borderRadius: '0 4px 4px 0'
          }}>
          <span style={{ color: PRIMARY, fontWeight: '700', fontSize: '15px' }}>{mod.title}</span>
        </div>

        {mod.days.map((day: any, dIdx: number) => (
          <div
            key={dIdx}
            style={{
              marginBottom: '12px',
              border: `1px solid ${BORDER}`,
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
            {/* Date row */}
            <div
              style={{
                backgroundColor: LIGHT_GRAY,
                textAlign: 'center',
                padding: '8px',
                fontWeight: '600',
                fontSize: '13px',
                color: PRIMARY,
                borderBottom: `1px solid ${BORDER}`
              }}>
              {day.date}
            </div>
            {/* Session rows */}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {day.slots.map((slot: any, sIdx: number) => (
                  <tr
                    key={sIdx}
                    style={{
                      borderBottom:
                        sIdx < day.slots.length - 1 ? `1px solid ${BORDER}` : 'none'
                    }}>
                    <td
                      style={{
                        padding: '10px 14px',
                        fontWeight: '600',
                        fontSize: '12.5px',
                        width: '190px',
                        borderRight: `1px solid ${BORDER}`,
                        backgroundColor: 'white',
                        verticalAlign: 'top'
                      }}>
                      {slot.session_name}
                    </td>
                    <td
                      style={{
                        padding: '10px 14px',
                        backgroundColor: 'white',
                        verticalAlign: 'top'
                      }}>
                      <div
                        style={{
                          fontWeight: '600',
                          fontSize: '13px',
                          color: DARK,
                          marginBottom: '2px'
                        }}>
                        {slot.topic}
                      </div>
                      <div style={{ fontSize: '12px', color: GRAY, fontStyle: 'italic' }}>
                        {slot.lecturers}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    ))}
  </div>
);

// --- FEEDBACK: MCQ table ---

const FeedbackMCQTable = ({ questions }: { questions: any[] }) => (
  <Table headers={['SN', 'Variables', 'Participants', 'Avg Score']}>
    {questions.map((q, i) => (
      <Tr key={i} even={i % 2 === 1}>
        <Td center style={{ width: '40px' }}>
          {i + 1}
        </Td>
        <Td>{q.question}</Td>
        <Td center style={{ width: '100px' }}>
          {q.participant_count}
        </Td>
        <Td center style={{ width: '100px', color: PRIMARY, fontWeight: '700' }}>
          {q.average_score.toFixed(2)}
        </Td>
      </Tr>
    ))}
  </Table>
);

// --- FEEDBACK: open-ended text questions ---

const FeedbackTextBlock = ({
  questions
}: {
  questions: { question: string; answers: string[] }[];
}) => (
  <>
    {questions.map((tq, i) => (
      <div key={i} style={{ marginBottom: '16px' }}>
        <div
          style={{
            color: PRIMARY,
            fontWeight: '700',
            fontSize: '13px',
            marginBottom: '6px',
            borderLeft: `3px solid ${PRIMARY}`,
            paddingLeft: '8px'
          }}>
          {tq.question}
        </div>
        <ul style={{ margin: '0 0 0 28px', padding: 0, listStyle: 'disc' }}>
          {tq.answers.map((ans, j) => (
            <li
              key={j}
              style={{
                fontSize: '12.5px',
                color: DARK,
                fontStyle: 'italic',
                marginBottom: '3px'
              }}>
              {ans}
            </li>
          ))}
        </ul>
      </div>
    ))}
  </>
);

// --- FULL FEEDBACK SECTION ---

export const FeedbackSection = ({ data }: { data: any }) => (
  <section style={{ marginBottom: '32px' }}>
    <SectionHeader title="5 Feedback Report" />

    {/* Module Review */}
    {data.module_feedbacks?.length > 0 && (
      <div style={{ marginBottom: '32px' }}>
        <div style={{ color: PRIMARY, fontWeight: '700', fontSize: '15px', marginBottom: '2px' }}>
          Module Review
        </div>
        <div style={{ color: GRAY, fontStyle: 'italic', fontSize: '12px' }}>
          Participants' views about the module
        </div>
        <div style={{ fontSize: '12px', color: DARK, marginBottom: '16px' }}>
          Note: Average score 4 is the Best Score in each criterion
        </div>

        {data.module_feedbacks.map((mf: any, i: number) => (
          <div key={i} style={{ marginBottom: '24px' }}>
            <div
              style={{
                backgroundColor: LIGHT_BLUE,
                borderLeft: `3px solid ${PRIMARY}`,
                padding: '8px 14px',
                marginBottom: '8px',
                fontSize: '13px',
                fontWeight: '600',
                color: PRIMARY,
                borderRadius: '0 4px 4px 0'
              }}>
              {mf.module_name}
            </div>
            {mf.mcq_questions?.length > 0 && <FeedbackMCQTable questions={mf.mcq_questions} />}
            {mf.text_questions?.length > 0 && <FeedbackTextBlock questions={mf.text_questions} />}
          </div>
        ))}
      </div>
    )}

    {/* Daily Session Feedback */}
    {data.session_feedbacks?.length > 0 && (
      <div style={{ marginBottom: '32px' }}>
        <div style={{ color: PRIMARY, fontWeight: '700', fontSize: '15px', marginBottom: '2px' }}>
          Daily Session Feedback
        </div>
        <div style={{ color: GRAY, fontStyle: 'italic', fontSize: '12px' }}>
          Participants' views about individual sessions
        </div>
        <div style={{ fontSize: '12px', color: DARK, marginBottom: '16px' }}>
          Note: Average score 4 is the Best Score in each criterion
        </div>

        {data.session_feedbacks.map((sf: any, i: number) => (
          <div key={i} style={{ marginBottom: '24px' }}>
            <div
              style={{
                backgroundColor: LIGHT_BLUE,
                borderLeft: `3px solid ${PRIMARY}`,
                padding: '10px 14px',
                marginBottom: '8px',
                borderRadius: '0 4px 4px 0'
              }}>
              <div style={{ color: PRIMARY, fontWeight: '600', fontSize: '12.5px' }}>
                {sf.date} / {sf.session_name}
              </div>
              <div style={{ fontWeight: '700', fontSize: '13px', color: DARK, marginTop: '2px' }}>
                Topic: {sf.topic}
              </div>
              <div style={{ fontStyle: 'italic', fontSize: '12px', color: GRAY }}>
                Resource Person: {sf.lecturer_name}
              </div>
            </div>
            {sf.mcq_questions?.length > 0 && <FeedbackMCQTable questions={sf.mcq_questions} />}
            {sf.text_questions?.length > 0 && <FeedbackTextBlock questions={sf.text_questions} />}
          </div>
        ))}
      </div>
    )}

    {/* Training Feedback */}
    {data.training_feedback?.mcq_questions?.length > 0 && (
      <div style={{ marginBottom: '32px' }}>
        <div style={{ color: PRIMARY, fontWeight: '700', fontSize: '15px', marginBottom: '2px' }}>
          Training Feedback
        </div>
        <div style={{ color: GRAY, fontStyle: 'italic', fontSize: '12px' }}>
          Participants' views about the overall Training
        </div>
        <div style={{ fontSize: '12px', color: DARK, marginBottom: '16px' }}>
          Note: Average score 4 is the Best Score in each criterion
        </div>
        <FeedbackMCQTable questions={data.training_feedback.mcq_questions} />
        {data.training_feedback.text_questions?.length > 0 && (
          <FeedbackTextBlock questions={data.training_feedback.text_questions} />
        )}
      </div>
    )}
  </section>
);

// --- ATTENDANCE TABLE ---

export const AttendanceTable = ({ trainees }: { trainees: any[] }) => (
  <Table headers={['SN', 'Name', 'Total', 'Present', 'Absent', 'Percentage']}>
    {trainees.map((t, i) => (
      <Tr key={i} even={i % 2 === 1}>
        <Td center style={{ width: '40px' }}>
          {i + 1}
        </Td>
        <Td>
          <strong>{t.full_name}</strong>
        </Td>
        <Td center>{t.total_days}</Td>
        <Td center>{t.present_days}</Td>
        <Td center>{t.absent_days}</Td>
        <Td center style={{ color: PRIMARY, fontWeight: '700' }}>
          {Math.round(t.attendance_percent)}%
        </Td>
      </Tr>
    ))}
  </Table>
);

// --- PRE/POST EVALUATION TABLE ---

export const PrePostTable = ({ trainees }: { trainees: any[] }) => (
  <Table headers={['SN', 'Trainee Name', 'Pre-Eval', 'Post-Eval', 'Improvement']}>
    {trainees.map((t, i) => {
      const pre = t.pre_evaluation?.percentage ?? null;
      const post = t.post_evaluation?.percentage ?? null;
      const diff = pre !== null && post !== null ? post - pre : null;

      return (
        <Tr key={i} even={i % 2 === 1}>
          <Td center style={{ width: '40px' }}>
            {i + 1}
          </Td>
          <Td>
            <strong>{t.full_name}</strong>
          </Td>
          <Td center>{pre !== null ? `${pre.toFixed(1)}%` : 'N/A'}</Td>
          <Td center>{post !== null ? `${post.toFixed(1)}%` : 'N/A'}</Td>
          <Td
            center
            style={{
              color: diff === null ? GRAY : diff >= 0 ? '#2e7d32' : '#c62828',
              fontWeight: '600'
            }}>
            {diff === null ? '-' : diff >= 0 ? `+${diff.toFixed(1)}%` : `${diff.toFixed(1)}%`}
          </Td>
        </Tr>
      );
    })}
  </Table>
);

// --- EVALUATOR TABLE ---

export const EvaluatorTable = ({ evaluations }: { evaluations: any[] }) => (
  <Table headers={['SN', 'Name', 'Total Marks', 'Obtained', 'Percentage', 'Rank']}>
    {evaluations.map((e, i) => (
      <Tr key={i} even={i % 2 === 1}>
        <Td center style={{ width: '40px' }}>
          {i + 1}
        </Td>
        <Td>
          <strong>{e.participant_name}</strong>
        </Td>
        <Td center>{e.total_marks}</Td>
        <Td center style={{ fontWeight: '700' }}>
          {e.obtain_marks}
        </Td>
        <Td center style={{ color: '#2e7d32', fontWeight: '700' }}>
          {e.percentage.toFixed(1)}%
        </Td>
        <Td center>{e.rank || '-'}</Td>
      </Tr>
    ))}
  </Table>
);

// --- STATISTICS SECTION ---

export const StatisticsSection = ({ stats }: { stats: any }) => {
  const StatTable = ({
    title,
    rows,
    federalRow
  }: {
    title: string;
    rows: { name: string; male: number; female: number }[];
    federalRow?: { name: string; male: number; female: number };
  }) => (
    <div style={{ marginBottom: '24px' }}>
      <div
        style={{
          borderLeft: `4px solid ${PRIMARY}`,
          paddingLeft: '10px',
          marginBottom: '10px'
        }}>
        <span style={{ fontWeight: '700', fontSize: '14px', color: DARK }}>{title}</span>
      </div>
      <Table headers={['SN', 'Group', 'Male', 'Female', 'Total']}>
        {federalRow && (
          <Tr>
            <Td center style={{ width: '40px' }}>
              -
            </Td>
            <Td>
              <strong>{federalRow.name}</strong>
            </Td>
            <Td center>{federalRow.male}</Td>
            <Td center>{federalRow.female}</Td>
            <Td center style={{ fontWeight: '700' }}>
              {federalRow.male + federalRow.female}
            </Td>
          </Tr>
        )}
        {rows.map((r, i) => (
          <Tr key={i} even={i % 2 === 1}>
            <Td center style={{ width: '40px' }}>
              {i + 1}
            </Td>
            <Td>
              <strong>{r.name}</strong>
            </Td>
            <Td center>{r.male}</Td>
            <Td center>{r.female}</Td>
            <Td center style={{ fontWeight: '700' }}>
              {r.male + r.female}
            </Td>
          </Tr>
        ))}
      </Table>
    </div>
  );

  return (
    <div>
      <StatTable title="Service Groups" rows={stats.service_groups || []} />
      <StatTable title="Ethnicity Breakdown" rows={stats.ethnicities || []} />
      <StatTable
        title="Federal & Provincial Distribution"
        rows={stats.provinces || []}
        federalRow={stats.federal}
      />
    </div>
  );
};
